import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { BellRing, Mail, Phone } from "lucide-react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";
import { formatParisDate, formatParisTime } from "./parisDateTime";
import { duePersonalReminders, type PersonalReminder } from "./personalReminders";
type ReminderContext = {
  rows: PersonalReminder[];
  error: string;
  create: (
    caseId: string,
    title: string,
    action: "phone" | "email",
    dueAt: string,
  ) => Promise<void>;
  update: (id: string, patch: { status?: "done" | "cancelled"; due_at?: string }) => Promise<void>;
};
const Context = createContext<ReminderContext | null>(null);
export const usePersonalReminders = () => useContext(Context);
export function PersonalRemindersProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return (
    <Controller key={user?.id || "anonymous"} userId={user?.id || null}>
      {children}
    </Controller>
  );
}
function Controller({ children, userId }: { children: ReactNode; userId: string | null }) {
  const [rows, setRows] = useState<PersonalReminder[]>([]),
    [error, setError] = useState(""),
    [now, setNow] = useState(Date.now()),
    [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const requestVersion = useRef(0);
  const location = useLocation();
  const refresh = useCallback(async () => {
    if (!userId) return;
    const version = ++requestVersion.current;
    const { data, error: e } = await supabase
      .from("personal_case_reminders")
      .select("id,user_id,case_id,case_title,action,due_at,status")
      .eq("user_id", userId)
      .eq("status", "pending")
      .order("due_at")
      .limit(500);
    if (!alive.current || version !== requestVersion.current) return;
    if (e) {
      setError("Impossible de charger vos rappels. Nouvelle tentative automatique.");
      return;
    }
    setRows(data || []);
    setError("");
    setNow(Date.now());
  }, [userId]);
  useEffect(() => {
    alive.current = true;
    if (!userId) return;
    void refresh();
    const timer = setInterval(() => void refresh(), 15000);
    const wake = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", wake);
    const channel = supabase
      .channel(`personal-reminders-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "personal_case_reminders",
          filter: `user_id=eq.${userId}`,
        },
        () => void refresh(),
      )
      .subscribe();
    return () => {
      alive.current = false;
      clearInterval(timer);
      window.removeEventListener("focus", wake);
      document.removeEventListener("visibilitychange", wake);
      void supabase.removeChannel(channel);
    };
  }, [refresh, userId]);
  useEffect(() => {
    const next = rows.find((r) => Date.parse(r.due_at) > Date.now());
    if (!next) return;
    const timer = setTimeout(
      () => setNow(Date.now()),
      Math.min(2147483647, Math.max(0, Date.parse(next.due_at) - Date.now() + 100)),
    );
    return () => clearTimeout(timer);
  }, [rows, now]);
  const update: ReminderContext["update"] = async (id, patch) => {
    if (!userId) throw new Error("Reconnectez-vous.");
    const { data, error: e } = await supabase
      .from("personal_case_reminders")
      .update(patch)
      .eq("id", id)
      .eq("user_id", userId)
      .eq("status", "pending")
      .select("id")
      .single();
    if (e || !data) throw new Error("La modification n’a pas été enregistrée. Réessayez.");
    await refresh();
  };
  const create: ReminderContext["create"] = async (caseId, title, action, dueAt) => {
    if (!userId) throw new Error("Reconnectez-vous.");
    const { error: e } = await supabase.from("personal_case_reminders").insert({
      user_id: userId,
      case_id: caseId,
      case_title: (title || "Dossier sans titre").slice(0, 500),
      action,
      due_at: dueAt,
    });
    if (e)
      throw new Error("Le rappel n’a pas été enregistré. Vérifiez votre connexion et la date.");
    await refresh();
  };
  const due = userId ? duePersonalReminders(rows, userId, now) : [];
  const current = due[0];
  const act = async (patch: Parameters<typeof update>[1]) => {
    if (!current) return;
    setBusy(true);
    try {
      await update(current.id, patch);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Réessayez.");
    } finally {
      setBusy(false);
    }
  };
  const staffPage = !/^\/(login|sign|avis|client)(\/|-|$)/.test(location.pathname);
  return (
    <Context.Provider value={{ rows, error, create, update }}>
      {children}
      {current && staffPage && (
        <aside
          role="alert"
          aria-label="Rappel personnel arrivé à échéance"
          className="fixed bottom-5 right-5 z-[120] w-96 rounded-xl border border-indigo-200 bg-white p-5 shadow-2xl"
        >
          <div className="flex items-center gap-2 text-indigo-700">
            <BellRing size={19} />
            <strong>C’est l’heure de votre rappel</strong>
            {due.length > 1 && <span className="ml-auto text-xs">{due.length} à traiter</span>}
          </div>
          <p className="mt-3 flex items-center gap-2 font-medium">
            {current.action === "phone" ? <Phone size={16} /> : <Mail size={16} />}{" "}
            {current.action === "phone" ? "Appel téléphonique" : "Email"}
          </p>
          <p className="mt-1 break-words text-sm text-gray-700">{current.case_title}</p>
          <p className="mt-1 text-xs text-gray-500">
            {formatParisDate(current.due_at)} à {formatParisTime(current.due_at)} · Paris
          </p>
          <Link
            to={`/reminders/case/${current.case_id}`}
            className="mt-3 inline-block text-sm font-medium text-indigo-700 underline"
          >
            Ouvrir le dossier
          </Link>
          {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
          <div className="mt-4 flex gap-2">
            <button
              disabled={busy}
              onClick={() => void act({ status: "done" })}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              Marquer comme fait
            </button>
            <button
              disabled={busy}
              onClick={() => void act({ due_at: new Date(Date.now() + 10 * 60000).toISOString() })}
              className="rounded-lg border px-3 py-2 text-sm disabled:opacity-50"
            >
              Dans 10 min
            </button>
          </div>
        </aside>
      )}
    </Context.Provider>
  );
}
